import React, { useState, useRef } from "react";
import { styled } from "@mui/material/styles";

import { TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { C2, H2, H3, RoundImage, Button, ImageUploadModal } from "../../common/commonComps";
import { uploadImage } from "../../common/utils";
import { AccountModal } from "../account/accountPieces";
import type { Size, Config, ImagePopItem } from "../../types";

const BtnWrap = styled(`div`)({
    padding: `20px`,
    textAlign: `center`,
    "& button": {
        margin: `0 10px`,
    },
});

const OuterBox = styled(`div`)({
    minHeight: `100px`,
});

interface WrapProps {
    pwidth?: number;
    pmargin?: number;
}

const Wrap = styled(`div`)<WrapProps>(({ pwidth, pmargin }) => ({
    maxWidth: `${pwidth}px`,
    margin: `${pmargin}px`,
    textAlign: `center`,
    cursor: `pointer`,
    display: pwidth ? `inline-block` : `none`,
    verticalAlign: `top`,
}));

interface ImagePopProps {
    size: Size;
    editable: boolean;
    v: string;
    pmargin?: number;
    pwidth?: number;
    config: Config;
    setContext: (nv: Partial<Config>) => void;
}

const ImagePop = ({ size, editable, v, pmargin, pwidth, config, setContext }: ImagePopProps): React.ReactElement => {
    const [editing, setEditing] = useState<boolean>(false);
    const [editingSubHeader, setEditingSubHeader] = useState<boolean>(false);
    const [imageUploadMdl, setImageUploadMdl] = useState<boolean>(false);
    const [buttonEditMdl, setButtonEditMdl] = useState<boolean>(false);
    const [fileLink, setFileLink] = useState<string | undefined>();
    const [file, setFile] = useState<File | undefined>();
    const [btnText, setBtnText] = useState<string>(``);
    const [btnLink, setBtnLink] = useState<string>(``);
    const [link, setLink] = useState<string>(``);

    const navigate = useNavigate();

    const imagePops = config?.imagePop || ({} as Record<string, ImagePopItem>);
    const imagePop: ImagePopItem = imagePops?.[v] ?? { text: `abc`, image: `http://cnn.com/logo` };
    const ipRef = useRef<HTMLDivElement>(null);
    const h2dref = useRef<HTMLDivElement>(null);
    const h2mref = useRef<HTMLDivElement>(null);

    const clearModal = () => {
        setFile(undefined);
        setFileLink(undefined);
        setImageUploadMdl(false);
        setButtonEditMdl(false);
    };

    const setImage = (e: React.ChangeEvent<HTMLInputElement>): void => {
        if (e.target.files) {
            const f = e.target.files[0];
            setFileLink(URL.createObjectURL(f));
            setFile(f);
        }
    };

    const sendImage = (): void => {
        uploadImage(file!).then((image: string) => {
            setFile(undefined);
            setFileLink(undefined);
            setImageUploadMdl(false);
            setContext({
                imagePop: { ...imagePops, [v]: { ...imagePop, image } },
            });
        });
    };

    return (
        <Wrap onClick={() => navigate(imagePop?.button?.link ?? ``)} {...{ pwidth, pmargin }} className="center">
            <AccountModal open={imageUploadMdl}>
                <ImageUploadModal
                    nolink
                    {...{ fileLink, context: imagePop, clearModal, link, setLink, sendImage, setImage }}
                />
            </AccountModal>
            <AccountModal autoFocus={false} open={buttonEditMdl}>
                <div>
                    Edit button text and link
                    <br />
                    <TextField
                        id="Text"
                        key="buttontext"
                        label="Button Text"
                        value={btnText}
                        margin="normal"
                        onChange={e => setBtnText(e.target.value)}
                        variant="outlined"
                        inputProps={{ style: { padding: `16px 17px` } }}
                    />
                    <br />
                    <TextField
                        id="Text"
                        key="buttonlink"
                        label="Button Link"
                        value={btnLink}
                        margin="normal"
                        onChange={e => setBtnLink(e.target.value)}
                        variant="outlined"
                        inputProps={{ style: { padding: `16px 17px` } }}
                    />
                    <BtnWrap>
                        <Button type="submit" onClick={clearModal}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            onClick={() => {
                                setContext({
                                    imagePop: {
                                        ...imagePops,
                                        [v]: { ...imagePop, button: { text: btnText, link: btnLink } },
                                    },
                                });
                                clearModal();
                            }}
                        >
                            Ok
                        </Button>
                    </BtnWrap>
                </div>
            </AccountModal>
            <div onClick={() => (editable ? setImageUploadMdl(true) : (() => {})())}>
                <RoundImage className="center" src={imagePop.image} />
            </div>
            {size === `small` && (
                <H3
                    ref={h2mref}
                    contentEditable={editingSubHeader}
                    suppressContentEditableWarning
                    dangerouslySetInnerHTML={{ __html: imagePop.subHeading ?? `` }}
                    onBlur={e => {
                        setContext({
                            imagePop: {
                                ...imagePops,
                                [v]: {
                                    ...imagePop,
                                    subHeading: e.target.innerHTML,
                                },
                            },
                        });
                        setEditing(false);
                    }}
                    {...(editable && !editingSubHeader
                        ? {
                              onClick: () => {
                                  setEditingSubHeader(true);
                                  setTimeout(() => {
                                      h2dref.current?.focus();
                                  }, 0);
                              },
                          }
                        : {})}
                    className={`center spacetop ${editingSubHeader ? `editing` : editable ? `edit` : ``}`}
                />
            )}
            {size !== `small` && (
                <OuterBox>
                    <div className="imagepoptext">
                        <H2
                            ref={h2dref}
                            contentEditable={editingSubHeader}
                            suppressContentEditableWarning
                            dangerouslySetInnerHTML={{ __html: imagePop.subHeading ?? `` }}
                            onBlur={e => {
                                setContext({
                                    imagePop: {
                                        ...imagePops,
                                        [v]: {
                                            ...imagePop,
                                            subHeading: e.target.innerHTML,
                                        },
                                    },
                                });
                                setEditing(false);
                            }}
                            {...(editable && !editingSubHeader
                                ? {
                                      onClick: () => {
                                          setEditingSubHeader(true);
                                          setTimeout(() => {
                                              h2dref.current?.focus();
                                          }, 0);
                                      },
                                  }
                                : {})}
                            className={`center ${editingSubHeader ? `editing` : editable ? `edit` : ``}`}
                        />

                        <C2
                            ref={ipRef}
                            contentEditable={editing}
                            suppressContentEditableWarning
                            dangerouslySetInnerHTML={{ __html: imagePop.text ?? `` }}
                            onBlur={e => {
                                setContext({
                                    imagePop: {
                                        ...imagePops,
                                        [v]: {
                                            ...imagePop,
                                            text: e.target.innerHTML,
                                        },
                                    },
                                });
                                setEditing(false);
                            }}
                            {...(editable && !editing
                                ? {
                                      onClick: () => {
                                          setEditing(true);
                                          setTimeout(() => {
                                              ipRef.current?.focus();
                                          }, 0);
                                      },
                                  }
                                : {})}
                            className={`${editing ? `editing` : editable ? `edit` : ``}`}
                        />
                    </div>
                </OuterBox>
            )}
        </Wrap>
    );
};

export default ImagePop;
